import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

interface Event {
  id: string;
  kind: string;
  at: string;
  actor: string;
  summary: string;
}

const KIND_THEME: Record<string, { icon: string; color: string }> = {
  note: { icon: '✎', color: 'var(--accent)' },
  note_deleted: { icon: '✕', color: 'var(--red)' },
  setting_change: { icon: '⚙', color: 'var(--blue)' },
  tag_added: { icon: '#', color: 'var(--terra)' },
  followup_created: { icon: '⏰', color: 'var(--amber)' },
  followup_completed: { icon: '✓', color: 'var(--green)' },
};

export function ActivityPanel({ customerId }: { customerId: string }) {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<{ events: Event[] }>(
      `/api/activity?customer_id=${encodeURIComponent(customerId)}&limit=50`,
    )
      .then((d) => {
        if (!cancelled) setEvents(d.events);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  return (
    <div className="section">
      <div className="section-header">
        <span className="section-label">Activity</span>
        <span className="section-meta">
          {loading ? 'Loading…' : `${events.length} events`}
        </span>
      </div>
      <div className="section-body">
        {error && <div className="banner banner-error">{error}</div>}
        {!loading && events.length === 0 ? (
          <div
            style={{
              fontSize: 'var(--fs-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            No activity recorded yet for this customer.
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 13,
                top: 12,
                bottom: 8,
                width: 1,
                background: 'var(--border-light)',
              }}
            />
            {events.map((e) => {
              const theme = KIND_THEME[e.kind] || {
                icon: '·',
                color: 'var(--text-muted)',
              };
              return (
                <li
                  key={e.id}
                  style={{
                    display: 'flex',
                    gap: 14,
                    padding: '8px 0',
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: 'var(--bg-card)',
                      border: `2px solid ${theme.color}`,
                      color: theme.color,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'var(--fs-base)',
                      fontWeight: 700,
                      lineHeight: 1,
                      zIndex: 1,
                      boxShadow: 'var(--shadow-1)',
                    }}
                  >
                    {theme.icon}
                  </span>
                  <div style={{ flex: 1, paddingTop: 3, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 'var(--fs-base)',
                        color: 'var(--text-primary)',
                        lineHeight: 1.45,
                      }}
                    >
                      {e.summary}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-muted)',
                        marginTop: 2,
                      }}
                    >
                      {e.actor} · {fmtRelative(e.at)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - t) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
  const diffDays = Math.round(diffMin / (60 * 24));
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(iso).toLocaleDateString();
}
